import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';

// Types
import type { LayoutAnalyticsType } from '@/lib/analytics/global';
import type {
    DatepickerComparisonPeriodsAvailableType,
    DatepickerPeriodsAvailableType
} from '@/lib/periods';
import type { DatepickerDataType } from '@/utils/hooks/datepicker/types';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

// Libs
import { localStorageDatepickers } from '@/lib/analytics/global';
import { LAST_30_DAYS, PREVIOUS_PERIOD } from '@/lib/periods';

dayjs.extend(utc);
dayjs.extend(timezone);

type localStorageDataType = Pick<DatepickerDataType, 'date'> & {
    period: DatepickerPeriodsAvailableType;
    compareTo: DatepickerComparisonPeriodsAvailableType;
};

export type AnalyticsPeriodSelectedType = localStorageDataType & {
    start: string;
    end: string;
};

const useAnalyticsPeriod = (type: LayoutAnalyticsType) => {
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    // Default value if the user doesn't have any localStorage data
    const defaultData: localStorageDataType = {
        period: LAST_30_DAYS,
        date: [
            dayjs()
                .utc()
                .tz(user?.settings.timezone)
                .subtract(29, 'days')
                .startOf('day')
                .format(),
            dayjs()
                .utc()
                .tz(user?.settings.timezone)
                .endOf('day')
                .format()
        ],
        compareTo: PREVIOUS_PERIOD
    };

    // Get localStorage data
    const localStorageDatepicker = localStorage.getItem(localStorageDatepickers[type]);
    const localStorageData: localStorageDataType = localStorageDatepicker
        ? JSON.parse(localStorageDatepicker)
        : defaultData;

    // Set period selected
    const [period, setPeriod] = useState<
        Pick<AnalyticsPeriodSelectedType, 'period' | 'date' | 'compareTo'>
    >({
        period: localStorageData.period,
        date: localStorageData.date,
        compareTo: localStorageData?.compareTo || null
    });

    return {
        periodSelected: {
            ...period,
            start: dayjs(period?.date?.[0]).format('YYYY-MM-DD') || null,
            end: dayjs(period?.date?.[1]).format('YYYY-MM-DD') || null
        },
        setPeriodSelected: setPeriod
    };
};

export default useAnalyticsPeriod;
